/**
 * Takeover — iter86.95 One UI Phase 0 primitive; iter87.17 unified
 * shell that matches the Draft-style tile modal (Power Up / Pot
 * Picks / Matchday Boost). iter87.59 rebuilds the entrance/exit on
 * top of Framer Motion via the shared BentoModal primitive so
 * opening and closing feel tactile and mirrored.
 *
 * Props unchanged:
 *   open, onClose, title, children, actionBar, testId, maxWidthClass
 *   icon, iconTone, pill
 *
 * Every consumer (all Cheer planes via DashboardTabs, the Create
 * Tournament flow, admin takeovers, and the pick/boost modals) still
 * inherits the same visual language — now with a matching close
 * animation instead of an abrupt unmount.
 */
import { ChevronLeft, ChevronDown } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";

const TONE_TILE = {
    cyber: "bg-cyber text-black",
    volt: "bg-volt text-black",
    blue: "bg-blue-500 text-white",
    blaze: "bg-blaze text-black",
    neutral: "bg-white/[0.08] text-white",
};

const TONE_RING = {
    cyber: "border-cyber/40",
    volt: "border-volt/40",
    blue: "border-blue-500/40",
    blaze: "border-blaze/40",
    neutral: "border-white/10",
};

export const Takeover = ({
    open,
    onClose,
    title,
    children,
    actionBar,
    testId = "takeover",
    maxWidthClass = "lg:max-w-3xl",
    icon: Icon,
    iconTone = "cyber",
    pill,
}) => {
    const hasIcon = !!Icon;
    const ringClass = hasIcon ? TONE_RING[iconTone] : "border-white/10";
    const tileClass = TONE_TILE[iconTone] || TONE_TILE.cyber;

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId={testId}
            backdropClassName="lg:items-start lg:justify-center lg:p-4 lg:pt-8"
            panelClassName={`flex-1 flex flex-col min-h-0 w-full lg:flex-none lg:h-auto lg:max-h-[calc(100dvh-4rem)] ${maxWidthClass} rounded-2xl border ${ringClass} bg-obsidian overflow-hidden`}
        >
            <div className="glass flex items-center gap-2 px-3 py-2.5 border-b border-white/10 shrink-0">
                {hasIcon ? (
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tileClass}`} aria-hidden="true">
                        <Icon className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid={`${testId}-back`}
                        aria-label="Back"
                        className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                )}
                <h2 className="font-display font-black text-base uppercase tracking-tight truncate flex-1 min-w-0">{title}</h2>
                {pill}
                {hasIcon && (
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid={`${testId}-close`}
                        aria-label="Close"
                        className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    >
                        <ChevronDown className="w-5 h-5" />
                    </button>
                )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">{children}</div>
            {actionBar && (
                <div className="glass border-t border-white/10 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shrink-0">
                    {actionBar}
                </div>
            )}
        </BentoModal>
    );
};

export default Takeover;
